import SchemaPlaceholderService from '../services/SchemaPlaceholderService'
import PageDataService from '../services/PageDataService'
import logger from './logger'

/** Contentful locale map keys look like `en-US` (max ~6 entries per field). */
const LOCALE_KEY_RE = /^[a-z]{2}(-[A-Z]{2})?$/

/** Process-lifetime cache for resolved schemas (skipped in preview). */
const jsonLdCache = new Map()
const JSON_LD_CACHE_MAX = 256

function withSlash(path) {
  return typeof path === 'string' && path.length > 0 && !path.endsWith('/')
    ? `${path}/`
    : path
}

function asList(x) {
  if (x == null) {
    return null
  }

  if (Array.isArray(x)) {
    return x.length ? x : null
  }

  return typeof x === 'object' ? [x] : null
}

/** Turn a Contentful JSON field into an object (handles locale wrapper + JSON string). */
function readObject(val) {
  if (val == null) {
    return null
  }

  let v = val
  if (v && typeof v === 'object' && !Array.isArray(v) && !v.sys) {
    const keys = Object.keys(v)
    if (
      keys.length > 0 &&
      keys.length <= 6 &&
      keys.every((k) => LOCALE_KEY_RE.test(k))
    ) {
      v = v['en-US'] ?? v['en-us'] ?? Object.values(v)[0]
    }
  }

  if (typeof v === 'string') {
    try {
      v = JSON.parse(v)
    } catch {
      return null
    }
  }

  return v && typeof v === 'object' ? v : null
}

/**
 * Schema blocks: after `parseEntry` the JSON field is usually `jsonld` on the object root
 * (see `parsers.test.js` “parseEntry maps array of linked Entry fields”). We still read
 * `jsonLd` / `fields.*` when a reference is not fully flattened.
 */
function ldFromBlock(block) {
  if (block == null) {
    return null
  }

  const f = block.fields

  return readObject(block.jsonld ?? block.jsonLd ?? f?.jsonld ?? f?.jsonLd)
}

export function normalizeJsonLdSchemaItems(raw) {
  if (!Array.isArray(raw) || !raw.length) {
    return null
  }

  const out = []
  for (const item of raw) {
    const direct = ldFromBlock(item)
    if (direct) {
      out.push({ jsonld: direct })
      continue
    }

    const kids = asList(item?.jsonLdSchemas ?? item?.fields?.jsonLdSchemas)
    if (!kids) {
      continue
    }

    for (const child of kids) {
      const parsed = ldFromBlock(child)
      if (parsed) {
        out.push({ jsonld: parsed })
      }
    }
  }

  return out.length ? out : null
}

function slug(s) {
  if (typeof s !== 'string' || !s.trim()) {
    return ''
  }

  return s
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 96)
}

function typeSlug(t) {
  return slug(Array.isArray(t) ? t.filter(Boolean).join('-') : String(t))
}

/** Prefer `@id`, then first `@graph` node, then `@type` for DOM id segment. */
function jsonLdDomIdSegment(jsonld) {
  let seg = typeof jsonld['@id'] === 'string' ? slug(jsonld['@id']) : ''
  if (seg) {
    return seg
  }

  const g = jsonld['@graph']
  if (Array.isArray(g) && g[0] && typeof g[0] === 'object') {
    const node = g[0]
    seg = typeof node['@id'] === 'string' ? slug(node['@id']) : ''
    if (seg) {
      return seg
    }

    if (node['@type']) {
      seg = typeSlug(node['@type'])
      if (seg) {
        return seg
      }
    }
  }

  if (jsonld['@type']) {
    seg = typeSlug(jsonld['@type'])
    if (seg) {
      return seg
    }
  }

  return ''
}

/** Stable `<script id>` fragment for one block. */
export function getContentfulJsonLdDomId(jsonld, index) {
  const n = Number.isFinite(index) ? index : 0
  const idFor = (seg) =>
    seg ? `jsonld-cms-${seg}-${n}` : `jsonld-cms-slot-${n}`

  if (!jsonld || typeof jsonld !== 'object') {
    return idFor('')
  }

  return idFor(jsonLdDomIdSegment(jsonld))
}

/** Extract JSON-LD from an already-fetched wwwPages payload (sync, no I/O). */
export function extractJsonLdFromPageData(pageData) {
  if (!pageData || typeof pageData !== 'object') {
    return null
  }

  for (const bucket of [pageData, pageData.model]) {
    if (!bucket) {
      continue
    }

    for (const part of [bucket.jsonLdSchemas, bucket.schemaEntity]) {
      const list = asList(part)
      if (list) {
        const blocks = normalizeJsonLdSchemaItems(list)
        if (blocks) {
          return blocks
        }
      }
    }
  }

  return null
}

function blocksFromPlaceholderRow(row) {
  return normalizeJsonLdSchemaItems(asList(row)) ?? null
}

async function fetchPlaceholderRow(pathNorm, preview) {
  return SchemaPlaceholderService.getInstance()
    .getByPath(pathNorm, preview)
    .catch(() => undefined)
}

/** wwwPages JSON-LD envelope via GraphQL (no REST). */
async function fetchPageEnvelope(pathNorm, preview) {
  try {
    return await PageDataService.getInstance().getJsonLdEnvelopeByPath(
      pathNorm,
      preview
    )
  } catch (error) {
    logger.error('getContentfulJsonLd: GraphQL envelope fetch failed', {
      path: pathNorm,
      preview,
      error,
    })

    throw error
  }
}

function readFromCache(pathNorm) {
  if (!jsonLdCache.has(pathNorm)) {
    return undefined
  }

  return jsonLdCache.get(pathNorm)
}

function writeToCache(pathNorm, blocks) {
  if (!blocks?.length) {
    return
  }

  if (jsonLdCache.size >= JSON_LD_CACHE_MAX) {
    const firstKey = jsonLdCache.keys().next().value
    jsonLdCache.delete(firstKey)
  }

  jsonLdCache.set(pathNorm, blocks)
}

/**
 * Page-linked schema wins over `schemaPlaceholderPage` (same as legacy order).
 */
function mergePageAndPlaceholder(pageData, placeholderRow) {
  const fromPage = extractJsonLdFromPageData(pageData)
  if (fromPage) {
    return fromPage
  }

  return blocksFromPlaceholderRow(placeholderRow)
}

async function resolveWithPlaceholderFallback(pageData, pathNorm, preview) {
  const fromPage = extractJsonLdFromPageData(pageData)
  if (fromPage) {
    return fromPage
  }

  const placeholderRow = await fetchPlaceholderRow(pathNorm, preview)

  return blocksFromPlaceholderRow(placeholderRow)
}

export function getJsonLdSchemasFromPostModel(item) {
  if (!item || typeof item !== 'object') {
    return null
  }

  for (const part of [item.jsonLdSchemas, item.schemaEntity]) {
    const list = asList(part)
    if (list) {
      const blocks = normalizeJsonLdSchemaItems(list)
      if (blocks) {
        return blocks
      }
    }
  }

  for (const key of ['jsonld', 'jsonLd']) {
    if (item[key] != null) {
      const blocks = normalizeJsonLdSchemaItems([{ jsonld: item[key] }])
      if (blocks) {
        return blocks
      }
    }
  }

  return null
}

export async function resolveContentfulJsonLdFromPageData(pageData, path) {
  return resolveWithPlaceholderFallback(pageData, withSlash(path), false)
}

/**
 * Resolve CMS JSON-LD for SSR.
 *
 * @param {string} path — normalized path with trailing slash
 * @param {{ pageData?: object, preview?: boolean }} [options]
 *   Pass `pageData` when GSSP already loaded the page via GraphQL to avoid a duplicate fetch.
 */
export async function getContentfulJsonLd(path, options = {}) {
  const { pageData: providedPageData, preview = false } = options
  const pathNorm = withSlash(path)

  if (providedPageData !== undefined) {
    return resolveWithPlaceholderFallback(providedPageData, pathNorm, preview)
  }

  if (!preview) {
    const cached = readFromCache(pathNorm)
    if (cached) {
      return cached
    }
  }

  const [pageEnvelope, placeholderRow] = await Promise.all([
    fetchPageEnvelope(pathNorm, preview),
    fetchPlaceholderRow(pathNorm, preview),
  ])

  const blocks = mergePageAndPlaceholder(pageEnvelope, placeholderRow)

  if (!preview && blocks) {
    writeToCache(pathNorm, blocks)
  }

  return blocks
}
